import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Collapse, Dialog, DialogActions, DialogContent, DialogTitle, Divider,
  Grid, IconButton, List, ListItem, ListItemText,
  Stack, Switch, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  Add, ArrowForward, Check, Delete,
  Edit, ExpandLess, ExpandMore, InfoOutlined, RestartAlt,
  Save, Star, Tune,
} from '@mui/icons-material';
import { adminApi } from '../../api/adminApi';

// ── Default feature-to-addon mapping (mirrors tenant portal defaults) ────────
const DEFAULT_ADDON_FEATURES = {
  'inventory-items': [
    'inventory.batch_operations', 'inventory.low_stock_alerts',
    'inventory.fifo_lifo_costing', 'inventory.reorder_point_automation',
    'inventory.negative_stock_allow', 'inventory.inventory_reservations',
    'inventory.bundle_products', 'inventory.unit_of_measure_conversion',
    'inventory.inventory_forecasting', 'inventory.cycle_count',
  ],
  'inventory-bundles': [
    'inventory.bundle_assembly',
  ],
  'lot-tracking': [
    'inventory.lot_number_tracking', 'inventory.expiry_date_tracking',
    'inventory.serial_number_tracking',
  ],
  'barcode-scanning': [
    'inventory.barcode_scanning', 'integrations.barcode_label_printing',
  ],
  'warehouses': [
    'warehousing.multi_warehouse', 'warehousing.bin_location_management',
    'warehousing.transfer_orders', 'warehousing.receiving_workflows',
    'warehousing.putaway_rules', 'warehousing.wave_picking',
    'warehousing.cross_docking', 'warehousing.zone_management',
  ],
  'history-retention': ['reporting.audit_log_export'],
  'scheduled-reports': [
    'reporting.scheduled_reports', 'reporting.custom_dashboards',
    'reporting.advanced_analytics', 'reporting.inventory_valuation_reports',
    'reporting.cost_analysis',
  ],
  'data-exports': [
    'reporting.export_csv', 'reporting.export_excel', 'reporting.export_pdf',
  ],
  'notifications': [
    'notifications.email_alerts', 'notifications.sms_alerts',
    'notifications.slack_integration', 'notifications.in_app_notifications',
    'notifications.push_notifications', 'notifications.digest_emails',
  ],
  'advanced-audit-trail': [
    'security.advanced_audit_log', 'security.data_masking',
    'security.field_level_encryption',
  ],
  'team-users': [
    'security.mfa_enforcement', 'security.ip_allowlisting',
    'security.sso_saml', 'security.session_timeout_policy',
    'licensing.multi_user_access',
  ],
  'api-requests': [
    'api_management.api_key_self_service', 'api_management.rate_limit_override',
    'api_management.graphql_api', 'api_management.webhook_delivery',
    'api_management.api_usage_analytics', 'integrations.webhooks',
    'integrations.public_api_access',
  ],
};

const DEFAULT_BASE_PLAN_FEATURES = [
  'integrations.ecommerce_connectors', 'integrations.erp_sync',
  'integrations.edi_support', 'integrations.accounting_integration',
  'integrations.shipping_carriers', 'licensing.usage_dashboard',
  'licensing.quota_warnings', 'licensing.self_service_billing',
  'licensing.custom_branding',
];

// Default features included in the base plan (used as fallback when basePlanFeatures not yet saved)
const DEFAULT_INCLUDED_FEATURE_KEYS = [
  'inventory.batch_operations', 'inventory.low_stock_alerts',
  'warehousing.receiving_workflows', 'reporting.export_csv',
  'integrations.public_api_access', 'security.session_timeout_policy',
  'notifications.email_alerts', 'notifications.in_app_notifications',
  'api_management.api_key_self_service', 'api_management.api_usage_analytics',
  'licensing.usage_dashboard', 'licensing.quota_warnings', 'licensing.multi_user_access',
];

const DEFAULT_ADDON_CATEGORIES = {
  'base-plan':            'Platform',
  'inventory-items':      'Inventory',
  'lot-tracking':         'Inventory',
  'barcode-scanning':     'Inventory',
  'inventory-bundles':    'Inventory',
  'warehouses':           'Warehousing',
  'history-retention':    'Reporting',
  'scheduled-reports':    'Reporting',
  'data-exports':         'Reporting',
  'notifications':        'Notifications',
  'advanced-audit-trail': 'Security',
  'team-users':           'Security',
  'api-requests':         'API & Integrations',
};

const CATEGORY_ORDER = ['All', 'Inventory', 'Warehousing', 'Reporting', 'Notifications', 'Security', 'API & Integrations', 'Platform'];

const LEVEL_ORDER = ['default', 'standard', 'professional', 'custom'];

const LEVEL_COLORS = {
  default:      'default',
  standard:     'primary',
  professional: 'secondary',
  custom:       'warning',
};

// ── Helper: build effective mapping from defaults + overrides ────────────────
function buildEffectiveMapping(featureMapping, customAddons, availableFeatures) {
  // Start with defaults
  const mapping = {};
  for (const [addonId, featureKeys] of Object.entries(DEFAULT_ADDON_FEATURES)) {
    for (const key of featureKeys) {
      mapping[key] = addonId;
    }
  }
  for (const key of DEFAULT_BASE_PLAN_FEATURES) {
    mapping[key] = 'base-plan';
  }
  // Apply overrides
  for (const [featureKey, addonId] of Object.entries(featureMapping || {})) {
    mapping[featureKey] = addonId;
  }
  // Any feature not mapped goes to base-plan
  for (const f of (availableFeatures || [])) {
    if (!mapping[f.key]) {
      mapping[f.key] = 'base-plan';
    }
  }
  return mapping;
}

function getFeaturesForAddon(addonId, effectiveMapping, availableFeatures) {
  return (availableFeatures || []).filter((f) => effectiveMapping[f.key] === addonId);
}

// ── Editable cell for limits/prices ─────────────────────────────────────────
function EditableCell({ value, codeDefault, isOverridden, onSave, suffix }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState('');

  const start = () => {
    setDraft(value === null ? '' : String(value));
    setEditing(true);
  };

  const commit = () => {
    const parsed = draft === '' ? null : Number(draft);
    if (draft !== '' && isNaN(parsed)) { return; }
    onSave(parsed);
    setEditing(false);
  };

  const reset = () => {
    onSave(undefined); // undefined = revert to code default
    setEditing(false);
  };

  if (editing) {
    return (
      <Stack direction="row" spacing={0.25} alignItems="center" sx={{ flexShrink: 0 }}>
        <TextField
          size="small"
          type="number"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { commit(); } if (e.key === 'Escape') { setEditing(false); } }}
          autoFocus
          placeholder="∞"
          sx={{ width: 60, '& input': { py: 0.25, fontSize: '0.72rem', textAlign: 'right' } }}
        />
        <Tooltip title="Save">
          <IconButton size="small" color="primary" onClick={commit} sx={{ p: 0.25 }}><Check sx={{ fontSize: 15 }} /></IconButton>
        </Tooltip>
        <Tooltip title={`Reset to default (${codeDefault === null ? '∞' : codeDefault})`}>
          <IconButton size="small" color="warning" onClick={reset} sx={{ p: 0.25 }}><RestartAlt sx={{ fontSize: 15 }} /></IconButton>
        </Tooltip>
      </Stack>
    );
  }

  return (
    <Stack direction="row" spacing={0.25} alignItems="center" sx={{ minWidth: 0 }}>
      <Typography variant="caption" noWrap sx={{ fontWeight: isOverridden ? 700 : 400 }}>
        {value === null ? '∞' : value}{suffix ? ` ${suffix}` : ''}
      </Typography>
      {isOverridden && (
        <Chip label="custom" size="small" color="warning" sx={{ fontSize: '0.55rem', height: 14 }} />
      )}
      <Tooltip title="Edit">
        <IconButton size="small" onClick={start} sx={{ p: 0.25, opacity: 0.5, '&:hover': { opacity: 1 } }}>
          <Edit sx={{ fontSize: 13 }} />
        </IconButton>
      </Tooltip>
    </Stack>
  );
}

// ── Create/Edit custom addon dialog ─────────────────────────────────────────
function CustomAddonDialog({ open, onClose, onSave, existing, validCategories }) {
  const [form, setForm] = useState({
    id: '', label: '', description: '', category: 'Inventory', type: 'display', unit: '', levels: null,
  });

  useEffect(() => {
    if (existing) {
      setForm({ ...existing });
    } else {
      setForm({
        id: '', label: '', description: '', category: 'Inventory',
        type: 'display', unit: '', levels: null,
      });
    }
  }, [existing, open]);

  const isEdit = Boolean(existing);
  const idError = !isEdit && !form.id.match(/^[a-z0-9-]+$/);

  const handleSave = () => {
    const addon = { ...form };
    if (!isEdit) {
      addon.id = form.id || form.label.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/-+$/, '');
    }
    if (addon.type === 'metered' && !addon.levels) {
      addon.levels = {
        default:      { limit: 0, price: 0 },
        standard:     { limit: 100, price: 9.99 },
        professional: { limit: null, price: 24.99 },
        custom:       { limit: null, price: null },
      };
      addon.unit = addon.unit || 'items';
    }
    if (addon.type === 'display') {
      delete addon.levels;
      delete addon.unit;
    }
    onSave(addon);
    onClose();
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Custom Add-On' : 'Create Custom Add-On'}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          {!isEdit && (
            <TextField
              label="ID (slug)"
              value={form.id}
              onChange={(e) => setForm((f) => ({ ...f, id: e.target.value }))}
              helperText="Lowercase letters, numbers, and hyphens only"
              error={form.id.length > 0 && idError}
              size="small"
              fullWidth
            />
          )}
          <TextField
            label="Display Name"
            value={form.label}
            onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
            size="small"
            fullWidth
            required
          />
          <TextField
            label="Description"
            value={form.description || ''}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            size="small"
            fullWidth
            multiline
            rows={2}
          />
          <TextField
            label="Category"
            select
            value={form.category || 'Inventory'}
            onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
            size="small"
            fullWidth
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            {(validCategories || CATEGORY_ORDER.filter((c) => c !== 'All')).map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </TextField>
          <TextField
            label="Type"
            select
            value={form.type || 'display'}
            onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}
            size="small"
            fullWidth
            SelectProps={{ native: true }}
            InputLabelProps={{ shrink: true }}
          >
            <option value="display">Display Only (feature grouping)</option>
            <option value="metered">Metered (with levels &amp; pricing)</option>
          </TextField>
          {form.type === 'metered' && (
            <TextField
              label="Unit"
              value={form.unit || ''}
              onChange={(e) => setForm((f) => ({ ...f, unit: e.target.value }))}
              size="small"
              fullWidth
              placeholder="e.g., items, scans/month, days"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={handleSave} disabled={!form.label}>
          {isEdit ? 'Save' : 'Create'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Add-on card (admin view) ────────────────────────────────────────────────
function AddonCard({
  addon, isBuiltIn, isBasePlan, features, featureLimitsData, pendingLimits,
  onLimitChange, onStartMove, onEditAddon, onDeleteAddon, categoryLabel,
  addonDisabled, onToggleAddon, disabledFeatures, onToggleFeature,
  basePlanPrice, basePlanPriceDefault, basePlanPriceIsOverridden, onBasePlanPriceChange,
  basePlanFeatures, onToggleBasePlanFeature, allFeatures,
  moveMode, isMoveSource, onSelectMoveTarget,
}) {
  const [expanded, setExpanded] = useState(false);

  const levelData = isBuiltIn && featureLimitsData
    ? featureLimitsData.find((f) => f.featureId === addon.id)
    : null;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: moveMode && !isMoveSource ? 'primary.main' : (isBasePlan ? 'success.main' : 'divider'),
        borderWidth: (isBasePlan || (moveMode && !isMoveSource)) ? 2 : 1,
        opacity: addonDisabled && !moveMode ? 0.5 : (moveMode && isMoveSource ? 0.6 : 1),
        transition: 'opacity 0.2s, border-color 0.2s',
      }}
    >
      {/* Move-mode destination selector */}
      {moveMode && !isMoveSource && (
        <Box
          onClick={() => onSelectMoveTarget(addon.id)}
          sx={{
            cursor: 'pointer', bgcolor: 'primary.main', color: 'primary.contrastText',
            px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1,
            '&:hover': { bgcolor: 'primary.dark' },
          }}
        >
          <ArrowForward sx={{ fontSize: 18 }} />
          <Typography variant="body2" fontWeight={700}>Move here</Typography>
        </Box>
      )}
      {moveMode && isMoveSource && (
        <Box sx={{ bgcolor: 'action.selected', px: 2, py: 1 }}>
          <Typography variant="caption" color="text.secondary" fontWeight={600}>
            Current card
          </Typography>
        </Box>
      )}
      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box flex={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>{addon.label}</Typography>
              {!isBuiltIn && !isBasePlan && (
                <Chip label="Custom" size="small" color="info" sx={{ fontSize: '0.65rem', height: 18 }} />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" display="block">
              {addon.description || ''}
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center">
            {isBasePlan && basePlanPrice !== undefined && (
              <Chip
                label={`$${(basePlanPrice ?? 0).toFixed(2)}/mo`}
                size="small"
                color="success"
                sx={{ fontWeight: 700, fontSize: '0.7rem' }}
              />
            )}
            {!isBasePlan && (
              <Tooltip title={addonDisabled ? 'Add-on hidden from tenants' : 'Add-on visible to tenants'}>
                <Switch
                  size="small"
                  checked={!addonDisabled}
                  onChange={() => onToggleAddon(addon.id)}
                />
              </Tooltip>
            )}
            <Chip label={categoryLabel} size="small" variant="outlined" sx={{ fontSize: '0.65rem', height: 20 }} />
            {!isBasePlan && !isBuiltIn && (
              <>
                <IconButton size="small" onClick={() => onEditAddon(addon)}>
                  <Edit sx={{ fontSize: 14 }} />
                </IconButton>
                <IconButton size="small" color="error" onClick={() => onDeleteAddon(addon.id)}>
                  <Delete sx={{ fontSize: 14 }} />
                </IconButton>
              </>
            )}
          </Stack>
        </Stack>

        {/* Monthly price (base plan card) — matches the Levels layout, positioned at top */}
        {isBasePlan && onBasePlanPriceChange && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Monthly Price
            </Typography>
            <Stack spacing={0.5} mt={0.5}>
              <Stack direction="row" justifyContent="space-between" alignItems="center"
                sx={{ px: 1, py: 0.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Chip
                  label="Base Plan"
                  size="small"
                  color="success"
                  sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20, width: 90 }}
                />
                <EditableCell
                  value={basePlanPrice}
                  codeDefault={basePlanPriceDefault}
                  isOverridden={basePlanPriceIsOverridden}
                  onSave={onBasePlanPriceChange}
                  suffix="$/mo"
                />
              </Stack>
            </Stack>
          </Box>
        )}

        {/* Level limits for built-in metered add-ons */}
        {levelData && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Levels
            </Typography>
            <Stack spacing={0.5} mt={0.5}>
              {levelData.levels.map((lvl) => {
                const { level, codeDefault } = lvl;
                // Reflect unsaved edits immediately. A pending field may be a
                // number, null (∞), or undefined (reset → code default).
                const pend = pendingLimits?.[addon.id]?.[level] || {};
                const limit = ('limit' in pend) ? (pend.limit === undefined ? codeDefault.limit : pend.limit) : lvl.limit;
                const price = ('price' in pend) ? (pend.price === undefined ? codeDefault.price : pend.price) : lvl.price;
                return (
                  <Stack key={level} direction="row" justifyContent="space-between" alignItems="center"
                    flexWrap="wrap" rowGap={0.5}
                    sx={{ px: 1, py: 0.5, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Chip
                      label={level === 'default' ? 'Basic' : level.charAt(0).toUpperCase() + level.slice(1)}
                      size="small"
                      color={LEVEL_COLORS[level] !== 'default' ? LEVEL_COLORS[level] : 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20, width: 76, flexShrink: 0 }}
                    />
                    <EditableCell
                      value={limit}
                      codeDefault={codeDefault.limit}
                      isOverridden={limit !== codeDefault.limit}
                      onSave={(v) => onLimitChange(addon.id, level, 'limit', v)}
                      suffix={levelData.unit || ''}
                    />
                    <EditableCell
                      value={price}
                      codeDefault={codeDefault.price}
                      isOverridden={price !== codeDefault.price}
                      onSave={(v) => onLimitChange(addon.id, level, 'price', v)}
                      suffix="$/mo"
                    />
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}

        {/* Level display for custom metered add-ons */}
        {!isBuiltIn && !isBasePlan && addon.type === 'metered' && addon.levels && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Levels
            </Typography>
            <Stack spacing={0.5} mt={0.5}>
              {LEVEL_ORDER.map((level) => {
                const cfg = addon.levels[level] || {};
                return (
                  <Stack key={level} direction="row" justifyContent="space-between" alignItems="center"
                    sx={{ px: 1, py: 0.25, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Chip
                      label={level === 'default' ? 'Basic' : level.charAt(0).toUpperCase() + level.slice(1)}
                      size="small"
                      color={LEVEL_COLORS[level] !== 'default' ? LEVEL_COLORS[level] : 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20, width: 90 }}
                    />
                    <Typography variant="caption">
                      {cfg.limit === null ? '∞' : (cfg.limit ?? '—')} {addon.unit || ''}
                    </Typography>
                    <Typography variant="caption">
                      {cfg.price === null ? 'Contact Sales' : cfg.price === 0 ? 'Included' : `$${cfg.price}/mo`}
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        )}
      </CardContent>

      {/* All features — categorized (base plan card) */}
      {isBasePlan && allFeatures && allFeatures.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{ px: 2, py: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => setExpanded((e) => !e)}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {basePlanFeatures.size} of {allFeatures.length} features in base plan
            </Typography>
            {expanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          <Collapse in={expanded}>
            <List dense disablePadding sx={{ px: 1, pb: 1, mt: 0.5, maxHeight: 500, overflow: 'auto' }}>
              {Object.entries(
                allFeatures.reduce((groups, f) => {
                  const cat = f.category || 'Other';
                  if (!groups[cat]) { groups[cat] = []; }
                  groups[cat].push(f);
                  return groups;
                }, {})
              ).map(([category, catFeatures]) => (
                <Box key={category} sx={{ mb: 0.5 }}>
                  <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.25, px: 1 }}>
                    {category}
                  </Typography>
                  {catFeatures.map(({ key, label }) => (
                    <ListItem key={key} disableGutters sx={{ py: 0, px: 1 }}>
                      <Switch
                        size="small"
                        checked={basePlanFeatures.has(key)}
                        onChange={() => onToggleBasePlanFeature(key)}
                        sx={{ mr: 0.5 }}
                      />
                      <ListItemText
                        primary={label}
                        primaryTypographyProps={{
                          variant: 'caption',
                          fontSize: '0.72rem',
                          sx: { opacity: basePlanFeatures.has(key) ? 1 : 0.6 },
                        }}
                      />
                    </ListItem>
                  ))}
                </Box>
              ))}
            </List>
          </Collapse>
        </>
      )}

      {/* Features list — collapsible (non-base-plan cards) */}
      {!isBasePlan && features.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{ px: 2, py: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => setExpanded((e) => !e)}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {features.length} feature{features.length !== 1 ? 's' : ''} assigned
            </Typography>
            {expanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          <Collapse in={expanded}>
            <List dense disablePadding sx={{ px: 1, pb: 1 }}>
              {/* Base Plan features grouped at the top, then the rest; each group
                  sorted alphabetically by label. */}
              {[...features].sort((a, b) => {
                const aBase = basePlanFeatures.has(a.key) ? 0 : 1;
                const bBase = basePlanFeatures.has(b.key) ? 0 : 1;
                if (aBase !== bBase) { return aBase - bBase; }
                return (a.label || '').localeCompare(b.label || '');
              }).map((f) => {
                const featureOff = disabledFeatures.includes(f.key);
                const inBasePlan = basePlanFeatures.has(f.key);
                return (
                  <ListItem
                    key={f.key}
                    disableGutters
                    sx={{ py: 0, px: 1, opacity: featureOff || addonDisabled || inBasePlan ? 0.5 : 1 }}
                    secondaryAction={
                      inBasePlan ? (
                        <Tooltip title="Included in Basic Inventory — disable there to edit here">
                          <Chip
                            icon={<Star sx={{ fontSize: 12 }} />}
                            label="Base Plan"
                            size="small"
                            color="success"
                            sx={{ fontSize: '0.6rem', height: 20 }}
                          />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Move">
                          <IconButton
                            edge="end"
                            size="small"
                            disabled={addonDisabled || moveMode}
                            onClick={() => onStartMove(f.key)}
                          >
                            <ArrowForward sx={{ fontSize: 14 }} />
                          </IconButton>
                        </Tooltip>
                      )
                    }
                  >
                    <Switch
                      size="small"
                      checked={!featureOff}
                      disabled={addonDisabled || inBasePlan}
                      onChange={() => onToggleFeature(f.key)}
                      sx={{ mr: 0.5 }}
                    />
                    <ListItemText
                      primary={f.label}
                      primaryTypographyProps={{ variant: 'caption', fontSize: '0.72rem' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </>
      )}
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function FeatureLimitsPage() {
  const [limitsData, setLimitsData] = useState(null);
  const [addonConfig, setAddonConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);
  const [pendingLimits, setPendingLimits] = useState({});
  const [categoryTab, setCategoryTab] = useState('All');

  // Feature move state — inline card-selection move mode
  const [movingFeature, setMovingFeature] = useState(null); // { key, label }
  const [movingFromAddon, setMovingFromAddon] = useState(null);

  // Custom addon dialog
  const [addonDialogOpen, setAddonDialogOpen] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);

  // Track unsaved addon config changes
  const [pendingMapping, setPendingMapping] = useState(null);
  const [pendingCustomAddons, setPendingCustomAddons] = useState(null);
  const [pendingDisabledAddons, setPendingDisabledAddons] = useState(null);
  const [pendingDisabledFeatures, setPendingDisabledFeatures] = useState(null);
  const [pendingBasePlanPrice, setPendingBasePlanPrice] = useState(null); // null = no change
  const [pendingBasePlanFeatures, setPendingBasePlanFeatures] = useState(null); // null = no change

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [limits, config] = await Promise.all([
        adminApi.getFeatureLimits(),
        adminApi.getAddonConfig(),
      ]);
      setLimitsData(limits);
      setAddonConfig(config);
      setPendingLimits({});
      setPendingMapping(null);
      setPendingCustomAddons(null);
      setPendingDisabledAddons(null);
      setPendingDisabledFeatures(null);
      setPendingBasePlanPrice(null);
      setPendingBasePlanFeatures(null);
    } catch {
      setError('Failed to load data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Build effective state
  const featureMapping = pendingMapping ?? addonConfig?.featureMapping ?? {};
  const customAddons = pendingCustomAddons ?? addonConfig?.customAddons ?? [];
  const availableFeatures = addonConfig?.availableFeatures ?? [];
  const validCategories = addonConfig?.validCategories ?? CATEGORY_ORDER.filter((c) => c !== 'All');

  const disabledAddons = pendingDisabledAddons ?? addonConfig?.disabledAddons ?? [];
  const disabledFeatures = pendingDisabledFeatures ?? addonConfig?.disabledFeatures ?? [];

  // Base plan features: use pending → saved config → defaults
  const basePlanFeaturesArray = pendingBasePlanFeatures
    ?? (addonConfig?.basePlanFeatures?.length ? addonConfig.basePlanFeatures : DEFAULT_INCLUDED_FEATURE_KEYS);
  const basePlanFeaturesSet = new Set(basePlanFeaturesArray);

  const effectiveMapping = buildEffectiveMapping(featureMapping, customAddons, availableFeatures);

  // Build list of all addon cards to show
  const allAddons = [
    { id: 'base-plan', label: 'Basic Inventory', description: 'Platform essentials included with every plan.', category: 'Platform', isBasePlan: true },
    ...(limitsData?.features || []).map((f) => ({
      id: f.featureId, label: f.label, description: f.description,
      category: DEFAULT_ADDON_CATEGORIES[f.featureId] || 'Platform',
      isBuiltIn: true,
    })),
    ...customAddons.map((a) => ({
      ...a,
      category: a.category || 'Platform',
      isBuiltIn: false,
      isBasePlan: false,
    })),
  ];

  // Filter by category tab, then order alphabetically by label with disabled
  // add-on cards sorted to the bottom (also alphabetical among themselves).
  const filteredAddons = allAddons
    .filter((a) => {
      if (categoryTab === 'All') { return true; }
      return a.category === categoryTab;
    })
    .sort((a, b) => {
      // Basic Inventory (base plan) always first.
      if (Boolean(a.isBasePlan) !== Boolean(b.isBasePlan)) { return a.isBasePlan ? -1 : 1; }
      const aDis = disabledAddons.includes(a.id);
      const bDis = disabledAddons.includes(b.id);
      if (aDis !== bDis) { return aDis ? 1 : -1; }
      return (a.label || '').localeCompare(b.label || '');
    });

  // Limit change handler
  const handleLimitChange = (featureId, level, field, value) => {
    setPendingLimits((prev) => ({
      ...prev,
      [featureId]: {
        ...(prev[featureId] || {}),
        [level]: {
          ...(prev[featureId]?.[level] || {}),
          [field]: value,
        },
      },
    }));
  };

  // Move feature handlers — inline card-selection flow
  const handleStartMove = (featureKey, fromAddonId) => {
    const feat = availableFeatures.find((f) => f.key === featureKey);
    setMovingFeature(feat || { key: featureKey, label: featureKey });
    setMovingFromAddon(fromAddonId);
  };

  const handleCancelMove = () => {
    setMovingFeature(null);
    setMovingFromAddon(null);
  };

  const handleSelectMoveTarget = (targetAddonId) => {
    if (!movingFeature || targetAddonId === movingFromAddon) { handleCancelMove(); return; }
    setPendingMapping((prev) => ({
      ...(prev ?? featureMapping),
      [movingFeature.key]: targetAddonId,
    }));
    handleCancelMove();
  };

  // Custom addon handlers
  const handleSaveCustomAddon = (addon) => {
    setPendingCustomAddons((prev) => {
      const list = [...(prev ?? customAddons)];
      const idx = list.findIndex((a) => a.id === addon.id);
      if (idx >= 0) {
        list[idx] = addon;
      } else {
        list.push(addon);
      }
      return list;
    });
  };

  const handleDeleteCustomAddon = (addonId) => {
    setPendingCustomAddons((prev) => {
      return (prev ?? customAddons).filter((a) => a.id !== addonId);
    });
    // Move any features assigned to this addon back to base-plan
    setPendingMapping((prev) => {
      const map = { ...(prev ?? featureMapping) };
      for (const [key, target] of Object.entries(map)) {
        if (target === addonId) {
          map[key] = 'base-plan';
        }
      }
      // Also check effective mapping for features assigned via defaults
      for (const [key, target] of Object.entries(effectiveMapping)) {
        if (target === addonId) {
          map[key] = 'base-plan';
        }
      }
      return map;
    });
  };

  // Toggle addon enabled/disabled
  const handleToggleAddon = (addonId) => {
    setPendingDisabledAddons((prev) => {
      const list = [...(prev ?? disabledAddons)];
      const idx = list.indexOf(addonId);
      if (idx >= 0) { list.splice(idx, 1); } else { list.push(addonId); }
      return list;
    });
  };

  // Base plan price change handler
  const handleBasePlanPriceChange = (value) => {
    // undefined = reset to code default (null to API), number = override
    setPendingBasePlanPrice(value === undefined ? 'reset' : value);
  };

  // Toggle individual feature enabled/disabled
  const handleToggleFeature = (featureKey) => {
    setPendingDisabledFeatures((prev) => {
      const list = [...(prev ?? disabledFeatures)];
      const idx = list.indexOf(featureKey);
      if (idx >= 0) { list.splice(idx, 1); } else { list.push(featureKey); }
      return list;
    });
  };

  // Toggle feature in/out of base plan
  const handleToggleBasePlanFeature = (featureKey) => {
    setPendingBasePlanFeatures((prev) => {
      const list = [...(prev ?? basePlanFeaturesArray)];
      const idx = list.indexOf(featureKey);
      if (idx >= 0) { list.splice(idx, 1); } else { list.push(featureKey); }
      return list;
    });
  };

  const hasPendingLimits = Object.keys(pendingLimits).length > 0;
  const hasPendingConfig = pendingMapping !== null || pendingCustomAddons !== null
    || pendingDisabledAddons !== null || pendingDisabledFeatures !== null
    || pendingBasePlanPrice !== null || pendingBasePlanFeatures !== null;
  const hasPending = hasPendingLimits || hasPendingConfig;

  const handleSave = async () => {
    setSaving(true);
    try {
      const promises = [];

      // Save limit changes
      if (hasPendingLimits) {
        const levels = {};
        for (const [featureId, levelMap] of Object.entries(pendingLimits)) {
          levels[featureId] = {};
          const feature = limitsData.features.find((f) => f.featureId === featureId);
          for (const [level, changes] of Object.entries(levelMap)) {
            const existing = feature?.levels.find((l) => l.level === level) || {};
            const cd = existing.codeDefault || {};
            // A field present with value `undefined` is a reset → code default;
            // present with a value is an override; absent → keep existing.
            levels[featureId][level] = {
              limit: ('limit' in changes) ? (changes.limit === undefined ? cd.limit : changes.limit) : existing.limit,
              price: ('price' in changes) ? (changes.price === undefined ? cd.price : changes.price) : existing.price,
            };
          }
        }
        promises.push(adminApi.updateFeatureLimits({ levels }));
      }

      // Save addon config changes
      if (hasPendingConfig) {
        const config = {};
        if (pendingMapping !== null) { config.featureMapping = pendingMapping; }
        if (pendingCustomAddons !== null) { config.customAddons = pendingCustomAddons; }
        if (pendingDisabledAddons !== null) { config.disabledAddons = pendingDisabledAddons; }
        if (pendingDisabledFeatures !== null) { config.disabledFeatures = pendingDisabledFeatures; }
        if (pendingBasePlanPrice !== null) {
          config.basePlanPrice = pendingBasePlanPrice === 'reset' ? null : pendingBasePlanPrice;
        }
        if (pendingBasePlanFeatures !== null) {
          config.basePlanFeatures = pendingBasePlanFeatures;
        }
        promises.push(adminApi.updateAddonConfig(config));
      }

      await Promise.all(promises);
      setToast({ severity: 'success', message: 'Changes saved successfully.' });
      await load();
    } catch (e) {
      setToast({ severity: 'error', message: e.message || 'Failed to save.' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box maxWidth={1400} mx="auto" px={3} py={4}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <Tune sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>Feature Limits & Add-Ons</Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" mt={0.5}>
            Configure add-on limits, pricing, and feature assignments. Changes appear on tenant portals.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            startIcon={<Add />}
            onClick={() => { setEditingAddon(null); setAddonDialogOpen(true); }}
          >
            Custom Add-On
          </Button>
          <Button variant="outlined" onClick={load} disabled={loading || saving}>
            Reload
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            disabled={!hasPending || saving}
            onClick={handleSave}
          >
            Save Changes
          </Button>
        </Stack>
      </Stack>

      {/* Category tabs */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={categoryTab}
          onChange={(_, v) => setCategoryTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' } }}
        >
          {CATEGORY_ORDER.map((cat) => (
            <Tab key={cat} label={cat} value={cat} />
          ))}
        </Tabs>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
      {toast && <Alert severity={toast.severity} sx={{ mb: 3 }} onClose={() => setToast(null)}>{toast.message}</Alert>}

      {!loading && limitsData && !movingFeature && (
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3, borderRadius: 2 }}>
          Click a level&apos;s limit or price to edit. Use the arrow icon on a feature to move it, then pick a destination card.
          {hasPending && <strong> You have unsaved changes.</strong>}
        </Alert>
      )}

      {movingFeature && (
        <Alert
          severity="info"
          icon={<ArrowForward />}
          sx={{
            mb: 3, borderRadius: 2, position: 'sticky', top: 8, zIndex: 5,
            bgcolor: 'primary.main', color: 'primary.contrastText',
            '& .MuiAlert-icon': { color: 'primary.contrastText' },
          }}
          action={
            <Button color="inherit" size="small" variant="outlined" onClick={handleCancelMove}>
              Cancel
            </Button>
          }
        >
          Moving <strong>{movingFeature.label}</strong> — select a destination add-on card below.
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={8}><CircularProgress /></Box>
      ) : (
        <Grid container spacing={3}>
          {filteredAddons.map((addon) => {
            const addonFeatures = getFeaturesForAddon(addon.id, effectiveMapping, availableFeatures);
            // Compute effective base plan price for the base-plan card
            const effectiveBasePlanPrice = pendingBasePlanPrice !== null
              ? (pendingBasePlanPrice === 'reset' ? limitsData?.basePlanPriceDefault : pendingBasePlanPrice)
              : limitsData?.basePlanPrice;
            return (
              <Grid item xs={12} sm={6} md={4} key={addon.id}>
                <AddonCard
                  addon={addon}
                  isBuiltIn={addon.isBuiltIn}
                  isBasePlan={addon.isBasePlan}
                  features={addonFeatures}
                  featureLimitsData={limitsData?.features}
                  pendingLimits={pendingLimits}
                  onLimitChange={handleLimitChange}
                  onStartMove={(featureKey) => handleStartMove(featureKey, addon.id)}
                  moveMode={Boolean(movingFeature)}
                  isMoveSource={Boolean(movingFeature) && movingFromAddon === addon.id}
                  onSelectMoveTarget={handleSelectMoveTarget}
                  onEditAddon={(a) => { setEditingAddon(a); setAddonDialogOpen(true); }}
                  onDeleteAddon={handleDeleteCustomAddon}
                  categoryLabel={addon.category || 'Platform'}
                  addonDisabled={disabledAddons.includes(addon.id)}
                  onToggleAddon={handleToggleAddon}
                  disabledFeatures={disabledFeatures}
                  onToggleFeature={handleToggleFeature}
                  basePlanPrice={effectiveBasePlanPrice}
                  basePlanPriceDefault={limitsData?.basePlanPriceDefault ?? limitsData?.basePlanPrice}
                  basePlanPriceIsOverridden={pendingBasePlanPrice !== null ? pendingBasePlanPrice !== 'reset' : limitsData?.basePlanPriceIsOverridden}
                  onBasePlanPriceChange={handleBasePlanPriceChange}
                  basePlanFeatures={basePlanFeaturesSet}
                  onToggleBasePlanFeature={handleToggleBasePlanFeature}
                  allFeatures={availableFeatures}
                />
              </Grid>
            );
          })}
        </Grid>
      )}

      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        Changes take effect within 5 minutes (in-memory cache TTL). Existing tenant sessions are unaffected until their next API call.
      </Typography>

      {/* Custom addon dialog */}
      <CustomAddonDialog
        open={addonDialogOpen}
        onClose={() => { setAddonDialogOpen(false); setEditingAddon(null); }}
        onSave={handleSaveCustomAddon}
        existing={editingAddon}
        validCategories={validCategories}
      />
    </Box>
  );
}
